import { AppHTTPService } from '@/api/http-service'
import { RenameDialog } from '@/components/RenameDialog/RenameDialog'
import { DropdownInput, Value } from '@flatfile/baseline'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Session, Sessions } from '../../types/Sessions'

export const SessionsDropdown = ({ sessions }: { sessions: Sessions }) => {
  const { projectId } = useParams()
  const [currentSession, setCurrentSession] = useState<Session | null>(null)
  const [isRenaming, setIsRenaming] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  useEffect(() => {
    const found = sessions?.find((session) => session.id === projectId) ?? null
    setCurrentSession(found)
  }, [projectId, sessions])

  const handleSessionChange = (value: Value[]) => {
    if (!value || value.length === 0) return
    navigate(`/projects/${value[0]}/prepare-chat`)
  }

  const handleRename = async (newName: string) => {
    if (!currentSession) return
    await AppHTTPService.client.projects.update(currentSession.id, {
      name: newName,
    })
    await queryClient.invalidateQueries({ queryKey: ['projects'] })
  }

  const sessionOptions = useMemo(
    () =>
      sessions?.map((session) => ({
        label: session.name,
        value: session.id,
      })) ?? [],
    [sessions]
  )

  return (
    <RenameDialog
      isOpen={isRenaming}
      onOpenChange={setIsRenaming}
      currentName={currentSession?.name || ''}
      onRename={handleRename}
      title='Project Name'
      trigger={
        <DropdownInput
          className='overflow-hidden flex-shrink-0 w-[200px] text-left'
          onValueChange={handleSessionChange}
          options={sessionOptions}
          matchTriggerWidth={false}
          value={currentSession?.id}
          defaultValue={projectId ? [projectId] : []}
          disableClear
          variant='ghost'
          onDoubleClick={() => setIsRenaming(true)}
        />
      }
    />
  )
}
